import React, {
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { WeekYear } from '../../date.utils';
import {
  CalendarNavigation,
  CalendarNavigationContext,
  NavigationActionType,
  ZoomFactor,
} from './useCalendarNavigation';
import {
  CalendarUhaContext,
  CalendarUhaData,
} from '../../calendaruha/calendarUhaContext';
import {
  earliestVisibleDate,
  generateKey,
  updateNavigationByCurrentWeek,
} from './utils';
import {
  ActivityStatusOptions,
  PracticeTypesPerInstitution,
} from '../../calendaruha/calendarModels';
import {
  EmneDto as UHAEmneDto,
  PraksiskalenderAktivitetDto as UHAPraksiskalenderAktivitetDto,
  PraksiskalenderDto as UHAPraksiskalenderDto,
  PraksisklasseDto as UHAPraksisklasseDto,
} from '../../api/generated/studiested-generated-api';
import {
  PraksisgruppeDto,
  PraksiskalenderAktivitetDto as PSAPraksiskalenderAktivitetDto,
  PraksiskalenderDto as PSAPraksiskalenderDto,
} from '../../api/generated/praksissted-generated-api';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import classNames from 'classnames';
import Timeline from './calendarTimelineHeader';
import Spinner from '../spinner/spinner';
import Calendars from '../../calendaruha/calendars';
import { EmptyState } from '../emptyState/emptyState';
import './calendar.module.scss';
import { useLoader } from '../../api/useLoader';
import {
  groupActivitiesByClass,
  groupClassesByInstitution,
  practicePeriodFilter,
  practiceTypeFiltersCombined,
} from '../../calendaruha/utils';
import { PraksisstedApiContext } from '../../api/PraksisstedApiContext';
import {
  CalendarPsaContext,
  CalendarPsaData,
} from '../../calendarpsa/calendarPsaContext';
import { SelectedRoleContext } from '../../providers';
import { RoletypeDto } from '../../api/generated/login-generated-api';
import { GenericActionWithPayload, groupBy } from '../../util';
import CalendarPerPraksissted from '../../calendarpsa/calendarPerPraksissted';
import {
  practiceActivitiesFilter,
  practiceGroupsFilter,
} from '../../calendarpsa/utils';

interface CalendarProps {
  emptyStateText: string;
  emptyStateTitle: string;
  startYear: number;
  navigation: CalendarNavigation;
  searchPhrase: string;
  activityStatusFilter: ActivityStatusOptions;
  onActivityStatusFilterChanged: Function;
}

interface PSACalendarProps extends CalendarProps {
  onCellClick?: never;
}

interface UHACalendarProps extends CalendarProps {
  onCellClick: (aktivitetId: string) => void;
}

const usePrevNavigation = (navigation: CalendarNavigation) => {
  const ref = useRef<CalendarNavigation>();

  useEffect(() => {
    ref.current = navigation;
  });

  return ref.current;
};

export type UHAKlasserMedEmne = {
  emne: UHAEmneDto;
  klasser: UHAPraksisklasseDto[];
};

export enum PracticeGroupManipulation {
  ADD,
  DELETE,
}

enum CalendarActionType {
  SET_CALENDAR_DATA,
  ADD_PRACTICE_GROUP,
  REMOVE_PRACTICE_GROUP,
}

type CalendarAction =
  | GenericActionWithPayload<
      CalendarActionType.SET_CALENDAR_DATA,
      UHAPraksiskalenderDto | PSAPraksiskalenderDto
    >
  | GenericActionWithPayload<
      CalendarActionType.ADD_PRACTICE_GROUP,
      UHAKlasserMedEmne
    >
  | GenericActionWithPayload<
      CalendarActionType.REMOVE_PRACTICE_GROUP,
      UHAKlasserMedEmne
    >;

const initReducerState: UHAPraksiskalenderDto | PSAPraksiskalenderDto = {
  aktiviteter: [],
  klasser: [],
  praksisgrupper: [],
  praksisstednavn: '',
};

const manipulatePracticeGroups = (
  state: UHAPraksiskalenderDto,
  klasserMedEmne: UHAKlasserMedEmne,
  action: PracticeGroupManipulation,
): UHAPraksiskalenderDto => {
  const { emne, klasser } = klasserMedEmne;

  return {
    ...state,
    klasser: state.klasser.map((klassePerEmne) => {
      const { emne: klasseEmne } = klassePerEmne;
      const { kode, navn } = klasseEmne;

      if (kode === emne.kode && navn === emne.navn) {
        const klasseIds = klasser.map((klasse) => klasse.id);

        return {
          ...klassePerEmne,
          klasser:
            action === PracticeGroupManipulation.ADD
              ? klassePerEmne.klasser.concat(klasser)
              : klassePerEmne.klasser.filter(
                  (klasse) => !klasseIds.includes(klasse.id),
                ),
        };
      }
      return klassePerEmne;
    }),
  };
};

const calendarReducer: Reducer<
  UHAPraksiskalenderDto | PSAPraksiskalenderDto,
  CalendarAction
> = (state, action) => {
  switch (action.type) {
    case CalendarActionType.SET_CALENDAR_DATA:
      return action.payload;
    case CalendarActionType.ADD_PRACTICE_GROUP:
      return manipulatePracticeGroups(
        state as UHAPraksiskalenderDto,
        action.payload,
        PracticeGroupManipulation.ADD,
      );
    case CalendarActionType.REMOVE_PRACTICE_GROUP:
      return manipulatePracticeGroups(
        state as UHAPraksiskalenderDto,
        action.payload,
        PracticeGroupManipulation.DELETE,
      );
  }

  return state;
};

let rerenderTimeout = setTimeout(() => {}, 0);
let cache: { [key: string]: CalendarUhaData | CalendarPsaData } = {};

const Calendar: React.FC<
  React.PropsWithChildren<UHACalendarProps | PSACalendarProps>
> = ({
  emptyStateText,
  emptyStateTitle,
  startYear,
  navigation,
  searchPhrase,
  onCellClick,
  activityStatusFilter,
  onActivityStatusFilterChanged,
}) => {
  const navigationDispatch = useContext(CalendarNavigationContext);
  const { praksiskalenderApi: UHAPraksiskalenderApi } =
    useContext(StudiestedApiContext);
  const { praksiskalenderApi: PSAPraksiskalenderApi } = useContext(
    PraksisstedApiContext,
  );
  const { type: roleType } = useContext(SelectedRoleContext)!;
  const isUhaUser = roleType === RoletypeDto.Studiested;

  const [practiceTypesOrGroupsPerInstOrg, setPracticeTypesOrGroupsPerInstOrg] =
    useState<PracticeTypesPerInstitution[] | PraksisgruppeDto[]>([]);
  const [
    practicePeriodsOrClassesPerInstOrg,
    setPracticePeriodsOrClassesPerInstOrg,
  ] = useState<{
    [klasseId: string]:
      | UHAPraksiskalenderAktivitetDto[]
      | PSAPraksiskalenderAktivitetDto[];
  }>({});

  const [highlightedWeek, setHighlightedWeek] = useState<WeekYear | null>(null);
  const [rerender, setRerender] = useState<boolean>(false);
  const [calendarContext, setCalendarContext] = useState<
    CalendarUhaData | CalendarPsaData | null
  >();
  const [praksisstedNavn, setPraksisstednavn] = useState<string>('');

  const prevNavigation = usePrevNavigation(navigation);
  const semester: 1 | 2 = 2;
  const fromDate = new Date().setFullYear(startYear, 6, 1);

  const { data, loading } = useLoader<
    UHAPraksiskalenderDto | PSAPraksiskalenderDto
  >(
    () =>
      (isUhaUser
        ? UHAPraksiskalenderApi
        : PSAPraksiskalenderApi
      ).praksiskalenderGet({
        queryParams: { fromDate },
      }),
    [startYear],
  );

  const [calendarData, dispatchCalendarData] = useReducer(
    calendarReducer,
    initReducerState,
  );

  useEffect(() => {
    if (data) {
      dispatchCalendarData({
        type: CalendarActionType.SET_CALENDAR_DATA,
        payload: data,
      });
    }
  }, [data]);

  const checkIfGroupCanBeSafelyDeletedFunc = useCallback(
    (klasseId: string) => !(klasseId in practicePeriodsOrClassesPerInstOrg),
    [practicePeriodsOrClassesPerInstOrg],
  );

  const manipulateGroup = (
    klasseMedEmne: UHAKlasserMedEmne,
    type: PracticeGroupManipulation,
  ) =>
    dispatchCalendarData({
      type:
        type === PracticeGroupManipulation.ADD
          ? CalendarActionType.ADD_PRACTICE_GROUP
          : CalendarActionType.REMOVE_PRACTICE_GROUP,
      payload: klasseMedEmne,
    });

  const getCalendarContext = useCallback(
    (
      weekNumbers: WeekYear[],
      filteredMode: boolean,
    ): CalendarUhaData | CalendarPsaData => {
      return {
        filteredMode,
        weekNumbers,
        isWeekly: navigation.zoomFactor === ZoomFactor.WEEKLY,
        earliestVisibleIsoDate:
          weekNumbers.length > 0 ? earliestVisibleDate(weekNumbers) : '',
        handleWeekHover: setHighlightedWeek,
        onCellClick: onCellClick,
        startYear: startYear,
        manipulateGroup,
      };
    },
    [navigation, setHighlightedWeek, onCellClick, startYear],
  );

  useEffect(() => {
    navigationDispatch({ type: NavigationActionType.RESET });
  }, [startYear, navigationDispatch]);

  const isFilteredMode = useMemo(
    () =>
      activityStatusFilter !== ActivityStatusOptions.UNFILTERED ||
      searchPhrase.length > 0,
    [activityStatusFilter, searchPhrase],
  );

  const calculateContext = useCallback(
    (forceRecalculate?: boolean) => {
      if (forceRecalculate) {
        cache = {};
      }

      const state = { displayedYear: startYear, semester };
      const props = { navigation };
      const filteredMode = isFilteredMode;
      const key = generateKey(state, navigation, filteredMode);

      let ctx = cache[key];
      if (!ctx || forceRecalculate) {
        ctx = getCalendarContext(
          updateNavigationByCurrentWeek(props, state, navigationDispatch),
          filteredMode,
        );

        cache[key] = ctx;
      }

      return ctx;
    },
    [
      startYear,
      semester,
      navigation,
      navigationDispatch,
      getCalendarContext,
      isFilteredMode,
    ],
  );

  useEffect(() => {
    if (calendarData && isUhaUser) {
      const uhaData = calendarData as UHAPraksiskalenderDto;
      setPracticeTypesOrGroupsPerInstOrg(
        groupClassesByInstitution(uhaData.klasser),
      );
      setPracticePeriodsOrClassesPerInstOrg(
        groupActivitiesByClass(uhaData.aktiviteter),
      );
    } else if (calendarData) {
      const psaData = calendarData as PSAPraksiskalenderDto;
      setPracticeTypesOrGroupsPerInstOrg(psaData.praksisgrupper);
      setPracticePeriodsOrClassesPerInstOrg(
        groupBy(psaData.aktiviteter, (v) => v.klasseId),
      );
      setPraksisstednavn(psaData.praksisstednavn);
    }
  }, [
    calendarData,
    setPracticeTypesOrGroupsPerInstOrg,
    setPracticePeriodsOrClassesPerInstOrg,
    isUhaUser,
  ]);

  const delayedRerender = () => {
    clearTimeout(rerenderTimeout);
    rerenderTimeout = setTimeout(() => {
      setRerender(true);
    }, 500);
  };

  useEffect(() => {
    window.addEventListener('resize', delayedRerender);

    return () => {
      window.removeEventListener('resize', delayedRerender);
    };
  });

  // UHA calendar filters
  const practicePeriodsFiltered = useMemo(
    () =>
      isUhaUser
        ? practicePeriodFilter(
            practicePeriodsOrClassesPerInstOrg as {
              [p: string]: UHAPraksiskalenderAktivitetDto[];
            },
            activityStatusFilter,
          )
        : {},
    [practicePeriodsOrClassesPerInstOrg, activityStatusFilter, isUhaUser],
  );

  const practiceTypesFiltered = useMemo(
    () =>
      isUhaUser
        ? practiceTypeFiltersCombined(
            practicePeriodsFiltered,
            practiceTypesOrGroupsPerInstOrg as PracticeTypesPerInstitution[],
            searchPhrase,
            activityStatusFilter,
          )
        : [],
    [
      practicePeriodsFiltered,
      practiceTypesOrGroupsPerInstOrg,
      searchPhrase,
      activityStatusFilter,
      isUhaUser,
    ],
  );

  // PSA calendar filters
  const practiceActivitiesFiltered = useMemo(
    () =>
      !isUhaUser
        ? practiceActivitiesFilter(
            practicePeriodsOrClassesPerInstOrg as {
              [p: string]: PSAPraksiskalenderAktivitetDto[];
            },
            activityStatusFilter,
          )
        : {},
    [practicePeriodsOrClassesPerInstOrg, activityStatusFilter, isUhaUser],
  );

  const practiceGroupsFiltered = useMemo(
    () =>
      !isUhaUser
        ? practiceGroupsFilter(
            practiceActivitiesFiltered,
            practiceTypesOrGroupsPerInstOrg as PraksisgruppeDto[],
            searchPhrase,
          )
        : [],
    [
      practiceActivitiesFiltered,
      practiceTypesOrGroupsPerInstOrg,
      searchPhrase,
      isUhaUser,
    ],
  );

  useEffect(() => {
    setCalendarContext(
      calculateContext(
        rerender || prevNavigation?.zoomFactor !== navigation.zoomFactor,
      ),
    );
    setRerender(false);
  }, [
    rerender,
    navigation,
    calculateContext,
    prevNavigation,
    practiceTypesFiltered,
    calendarContext,
  ]);

  const { weekNumbers } = calendarContext || {};
  const isLoading =
    loading ||
    prevNavigation?.zoomFactor !== navigation.zoomFactor ||
    !calendarContext;

  return (
    <div
      className={classNames('calendar-results', `calendar-year-${startYear}`)}
    >
      <Timeline
        weekNumbers={weekNumbers || []}
        isWeekly={navigation.zoomFactor === ZoomFactor.WEEKLY}
        highlightedWeek={highlightedWeek}
        startYear={startYear}
      />
      {isLoading && <Spinner />}
      {practiceTypesFiltered.length > 0 || practiceGroupsFiltered.length > 0
        ? !isLoading &&
          (isUhaUser ? (
            <CalendarUhaContext.Provider
              value={calendarContext as CalendarUhaData}
            >
              <Calendars
                practiceTypesFiltered={practiceTypesFiltered}
                practicePeriodsFiltered={practicePeriodsFiltered}
                onActivityStatusFilterChanged={onActivityStatusFilterChanged}
                checkIfGroupCanBeSafelyDeleted={
                  checkIfGroupCanBeSafelyDeletedFunc
                }
              />
            </CalendarUhaContext.Provider>
          ) : (
            <CalendarPsaContext.Provider
              value={calendarContext as CalendarPsaData}
            >
              <CalendarPerPraksissted
                orderNo={0}
                praksisstednavn={praksisstedNavn}
                praksisgrupperFiltered={practiceGroupsFiltered}
                practiceActivitiesFiltered={practiceActivitiesFiltered}
              />
            </CalendarPsaContext.Provider>
          ))
        : !isLoading && (
            <EmptyState title={emptyStateTitle} text={emptyStateText} />
          )}
    </div>
  );
};

export default Calendar;
